import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AddOtherMemberForm.scss";
import {
  BENEFICIARY,
  CPA,
  FEMALE,
  GENDER_OTHER,
  MALE,
  NON_BINARY,
  OTHER,
  POA,
  PREFER_NOT_TO_SAY,
} from "../../Utilities/constants";
import { isEmailValid, validateMember } from "../../Utilities/validation";
import {
  formatBEDateStringToFE,
  formatFEDateStringToBE,
  formatTimestampToDate,
} from "../../Utilities/formatter";
import FormControl from "@mui/material/FormControl";
import states_list from "../../Data/us_states.json";
import country_list from "../../Data/country_list.json";
import ClientStatus from "../../Data/client_status.json";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextBox from "../../UX/TextBox/TextBox";
import Button from "../../UX/Button/Button";
import DropDownList from "../../UX/DropDownList/DropDownList";
import MaskedTextBox from "../../UX/MaskedTextBox/MaskedTextBox";
import NativeDatePicker from "../../UX/NativeDatePicker/NativeDatePicker";
import {Paper} from "@mui/material";

const AddOtherMemberForm = (props) => {
  const { t } = useTranslation();

  const RELATIONSHIP_DDL = [
    {
      id: BENEFICIARY,
      name: t("ClientDetails.Profile.Other.Relationship.beneficiary"),
    },
    { id: CPA, name: t("ClientDetails.Profile.Other.Relationship.cpa") },
    { id: POA, name: t("ClientDetails.Profile.Other.Relationship.poa") },
    { id: OTHER, name: t("ClientDetails.Profile.Other.Relationship.other") },
  ];

  const GENDER_DDL = [
    MALE,
    FEMALE,
    NON_BINARY,
    GENDER_OTHER,
    PREFER_NOT_TO_SAY,
  ];

  useEffect(() => {
    let newState = { ...state };
    newState.member = getMemberTemplate(props.member);
    setState(newState);
  }, []);

  const getMemberTemplate = (member) => {
    if (member == null || member.personId == null)
      return getNewMemberTemplate();

    return {
      firstName: {
        value: member ? member.firstName : "",
        isValid: true,
      },
      lastName: {
        value: member ? member.lastName : "",
        isValid: true,
      },
      middleName: {
        value: member ? member.middleName : "",
        isValid: true,
      },
      gender: {
        value: member ? member.gender : MALE,
        isValid: true,
      },
      genderOther: {
        value: member ? member.genderOther : "",
        isValid: true,
      },
      dateOfBirth: {
        value: member ? formatBEDateStringToFE(member.dateOfBirth) : null,
        isValid: true,
      },
      status: {
        value: member && member.status ? member.status : ClientStatus.status[0],
        isValid: true,
      },
      relationShip: member.relationShip,
      relationshipOther: member.relationshipOther,
      notes: member.notes,
      company:
        member && member.company
          ? member.company
          : {
              companyId: null,
              name: "",
              website: "",
            },
      emailList:
        member && member.emailList
          ? member.emailList
          : [
              {
                emailAddressId: null,
                emailAddress: null,
                emailAddressType: "HOME",
                isDeleted: false,
              },
            ],
      phoneList:
        member && member.phoneList
          ? member.phoneList
          : [
              {
                phoneNumberId: null,
                phoneNumber: "",
                phoneNumberType: "PERSONAL",
                isDeleted: false,
              },
            ],
      addressList:
        member && member.addressList
          ? member.addressList
          : [
              {
                addressId: null,
                addressLine: null,
                city: null,
                state: {
                  name: "",
                  "alpha-2": "",
                },
                country: {
                  id: "USA",
                  name: "United States",
                },
                zipCode: null,
              },
            ],
    };
  };

  const getNewMemberTemplate = () => {
    return {
      firstName: {
        value: "",
        isValid: true,
      },
      lastName: {
        value: "",
        isValid: true,
      },
      middleName: {
        value: "",
        isValid: true,
      },
      gender: {
        value: MALE,
        isValid: true,
      },
      genderOther: {
        value: "",
        isValid: true,
      },
      dateOfBirth: {
        value: null,
        isValid: true,
      },
      status: {
        value: ClientStatus.status[0],
        isValid: true,
      },
      relationShip: {
        id: BENEFICIARY,
        name: t("ClientDetails.Profile.Family.Relationship.beneficiary"),
      },
      relationshipOther: "",
      notes: null,
      company: {
        companyId: null,
        name: "",
        website: "",
      },
      emailList: [
        {
          emailAddressId: null,
          emailAddress: null,
          emailAddressType: "HOME",
          isDeleted: false,
        },
      ],
      phoneList: [
        {
          phoneNumberId: null,
          phoneNumber: "",
          phoneNumberType: "PERSONAL",
          isDeleted: false,
        },
      ],
      addressList: [
        {
          addressId: null,
          addressLine: null,
          city: null,
          state: {
            name: "",
            "alpha-2": "",
          },
          country: {
            id: "USA",
            name: "United States",
          },
          zipCode: null,
        },
      ],
      isNew: true,
    };
  };

  const [state, setState] = useState({
    member: getMemberTemplate(props.member),
  });

  let openedDropdownListRef = null;
  const setOpenedDropdownList = (ref) => {
    openedDropdownListRef = ref;
  };

  const hideDropdownOptions = (event) => {
    if (
      event.target.offsetHeight > 250 &&
      openedDropdownListRef &&
      openedDropdownListRef.state.opened
    ) {
      openedDropdownListRef.setState({
        focused: false,
        opened: false,
      });
    }
  };

  const update = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey].value = value;
    newState[primaryKey][subKey].isValid = true;
    setState(newState);
  };

  const updateValue = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey] = value;
    setState(newState);
  };

  const updateContact = (index, primaryKey, subKey, value) => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"][primaryKey][index][subKey] = value;
    if (subKey === "country") {
      if (value.id === "USA") {
        newState["member"]["addressList"][index]["state"] = {
          name: "",
          "alpha-2": "",
        };
      } else {
        newState["member"]["addressList"][index]["state"] = "";
      }
    }
    setState(newState);
  };

  const updateDateOfBirth = (primaryKey, subKey) => {
    return (date) => {
      // make sure we are updating with a valid date and not the keyboard event.
      if (date && date.target) {
        return;
      }

      let newState = { ...state };
      newState[primaryKey].isNew = false;
      newState[primaryKey][subKey].value = date;
      newState[primaryKey][subKey].isValid = true;
      setState(newState);
    };
  };

  const updateCompany = (key, value) => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"]["company"][key] = value;
    setState(newState);
  };

  const save = () => {
    if (validate()) {
      const memberData = generateNewMemberParams();
      props.save(memberData);
    }
  };

  const validate = () => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"] = validateMember(newState["member"]);

    let isValid =
      newState["member"]["firstName"].isValid &&
      newState["member"]["lastName"].isValid &&
      newState["member"]["dateOfBirth"].isValid;
    setState(newState);

    return isValid;
  };

  const generateNewMemberParams = () => {
    if (props.member != null && props.member.personId != null) {
      let member = { ...props.member };
      member.personId = props.member.personId;
      member.firstName = state.member.firstName.value;
      member.middleName = state.member.middleName.value;
      member.lastName = state.member.lastName.value;
      member.dateOfBirth = state.member.dateOfBirth.value
        ? formatFEDateStringToBE(state.member.dateOfBirth.value)
        : null;
      member.gender = state.member.gender.value;
      member.genderOther = state.member.genderOther.value;
      member.relationShip = state.member.relationShip;
      member.relationshipOther = state.member.relationshipOther;
      member.notes = state.member.notes;
      member.company = {
        companyId: state.member.company.companyId,
        name: state.member.company.name,
        website: state.member.company.website,
      };
      member.addressList = state.member.addressList.map((address) => {
        return {
          addressId: address.addressId,
          country: address.country.id,
          addressLine: address.addressLine,
          city: address.city,
          zipCode: address.zipCode,
          state:
            address.country.id === "USA"
              ? address.state["alpha-2"]
              : address.state,
        };
      });
      member.emailList = state.member.emailList.map((email) => {
        return {
          emailAddressId: email.emailAddressId,
          emailAddressType: "HOME",
          emailAddress: email.emailAddress,
        };
      });
      member.phoneList = state.member.phoneList.map((phone) => {
        return {
          phoneNumberId: phone.phoneNumberId,
          phoneNumberType: "PERSONAL",
          phoneNumber: phone.phoneNumber,
        };
      });
      return member;
    } else {
      return {
        personId: null,
        firstName: state.member.firstName.value,
        middleName: state.member.middleName.value,
        lastName: state.member.lastName.value,
        salutation: null,
        dateOfBirth: state.member.dateOfBirth.value
          ? formatFEDateStringToBE(state.member.dateOfBirth.value)
          : null,
        gender: state.member.gender.value,
        genderOther: state.member.genderOther.value,
        company: {
          companyId: null,
          name: state.member.company.name,
          website: state.member.company.website,
        },
        relationShip: state.member.relationShip,
        relationshipOther: state.member.relationshipOther,
        notes: state.member.notes,
        addressList: state.member.addressList.map((address) => {
          return {
            addressId: address.addressId,
            country: address.country.id,
            addressLine: address.addressLine,
            city: address.city,
            zipCode: address.zipCode,
            state:
              address.country.id === "USA"
                ? address.state["alpha-2"]
                : address.state,
          };
        }),
        emailList: state.member.emailList.map((email) => {
          return {
            emailAddressId: email.emailAddressId,
            emailAddressType: "HOME",
            emailAddress: email.emailAddress,
          };
        }),
        phoneList: state.member.phoneList.map((phone) => {
          return {
            phoneNumberId: phone.phoneNumberId,
            phoneNumberType: "PERSONAL",
            phoneNumber: phone.phoneNumber,
          };
        }),
      };
    }
  };

  const setupStateComponent = (address, index) => {
    let clientStateField;
    if (address.country.id === "USA") {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-dropdown"
        >
          <Typography component={"label"}>State</Typography>
          <DropDownList
            placeholder={t("Global.state")}
            value={address.state}
            onChange={(event) =>
              updateContact(index, "addressList", "state", event.target.value)
            }
            data={states_list}
            dataItemKey={"alpha-2"}
            textField={"name"}
            reference={setOpenedDropdownList}
          />
        </FormControl>
      );
    } else {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-textbox"
        >
          <Typography component={"label"}>State</Typography>
          <TextBox
            placeholder={t("Global.state")}
            minLength={0}
            maxLength={30}
            value={address.state}
            onChange={(event) =>
              updateContact(index, "addressList", "state", event.target.value)
            }
          />
        </FormControl>
      );
    }

    return clientStateField;
  };

  const setupAddressListComponent = (type) => {
    const addressListComponent = state[type].addressList.map(
      (address, index) => {
        return (
          <React.Fragment>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  label={t("Global.address")}
                  minLength={0}
                  maxLength={30}
                  value={address.addressLine}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "addressLine",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  label={t("Global.city")}
                  minLength={0}
                  maxLength={30}
                  value={address.city}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "city",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              {setupStateComponent(address, index)}
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Zip Code</Typography>
                <TextBox
                  placeholder={t("Global.zip_code")}
                  minLength={0}
                  maxLength={10}
                  value={address.zipCode}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "zipCode",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Country</Typography>
                <DropDownList
                  placeholder={t("Global.country")}
                  value={address.country}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "country",
                      event.target.value
                    )
                  }
                  data={country_list}
                  dataItemKey={"id"}
                  textField={"name"}
                  reference={setOpenedDropdownList}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} className="address-divider" />
          </React.Fragment>
        );
      }
    );
    return addressListComponent;
  };

  const setupEmailListComponent = (type) => {
    const emailListComponent = state[type].emailList.map((email, index) => {
      return (
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-95-width">
            <TextBox
              label={t("Global.email")}
              minLength={0}
              maxLength={50}
              value={email.emailAddress}
              onChange={(event) =>
                updateContact(
                  index,
                  "emailList",
                  "emailAddress",
                  event.target.value
                )
              }
              validate={(value) => {
                if (!(isEmailValid(value) || value === ""))
                  return "Email is not valid";
              }}
            />
          </FormControl>
        </Grid>
      );
    });
    return emailListComponent;
  };

  const setupPhoneListComponent = (type) => {
    const phoneListComponent = state[type].phoneList.map((phone, index) => {
      return (
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-95-width">
            <MaskedTextBox
              label={t("Global.phone")}
              mask={"999-000-0000"}
              value={phone.phoneNumber}
              onChange={(event) =>
                updateContact(
                  index,
                  "phoneList",
                  "phoneNumber",
                  event.target.value
                )
              }
            />
          </FormControl>
        </Grid>
      );
    });
    return phoneListComponent;
  };

  const setupContactInfoComponent = (type) => {
    const contactComponents = (
      <React.Fragment>
        <Grid item xs={12} style={{ marginTop: "20px" }}>
          <Typography
            component={"p"}
            variant={"body1"}
            color={"primary"}
            style={{ cursor: "pointer" }}
            className={"client-form-section-header"}
          >
            {t("Clients.contact_info_optional_fields")}
          </Typography>
        </Grid>
        {setupEmailListComponent("member")}
        {setupPhoneListComponent("member")}
        {setupAddressListComponent("member")}

      </React.Fragment>
    );

    return contactComponents;
  };

  const modalTitle = props.member
    ? t("ClientDetails.Profile.Other.editOther")
    : t("ClientDetails.Profile.Other.addOther");
  let buttonsGroup = (
    <React.Fragment>
      <span style={{ marginLeft: "10px" }}>
        <Button label={props.saveLabel ? props.saveLabel : "Save"} primary={true} outline={true} onClick={save} />
      </span>
    </React.Fragment>
  );

  let clientFirstNameComponent;
  if (props.member) {
    clientFirstNameComponent = (
      <TextBox
        label={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.member.firstName.value}
        onChange={(event) => update("member", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.member.firstName.isValid}
        autoFocus
      />
    );
  } else {
    clientFirstNameComponent = (
      <TextBox
        label={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.member.firstName.value}
        onChange={(event) => update("member", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.member.firstName.isValid}
      />
    );
  }

  let relationshipOtherComponent;
  if (state.member.relationShip.id === OTHER) {
    relationshipOtherComponent = (
      <FormControl margin="normal" required className="percent-95-width">
        <TextBox
          label={t("ClientDetails.Profile.Family.relationship_type")}
          minLength={0}
          maxLength={30}
          value={state.member.relationshipOther}
          onChange={(event) =>
            updateValue("member", "relationshipOther", event.target.value)
          }
        />
      </FormControl>
    );
  }

  let content = (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item xs={8}></Grid>
        <Grid
          item
          xs={4}
          className="buttons-group"
          style={{ textAlign: "right" }}
        >
          <div className={"client-wizard-buttons"}>{buttonsGroup}</div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        className="add-other-member-modal-div"
        onScroll={hideDropdownOptions}
      >
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography component={"p"} variant={"body1"} color={"primary"} className={"client-form-section-header"}>
                      {t("Clients.basic_information")}
                      <span className="required-condition">
                  {t("Clients.basic_information_required")}
                </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      {clientFirstNameComponent}
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          label={t("Clients.middle_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.middleName.value}
                          onChange={(event) =>
                              update("member", "middleName", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          label={t("Clients.last_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.lastName.value}
                          onChange={(event) =>
                              update("member", "lastName", event.target.value)
                          }
                          validate={(value) => {
                            if (value === "") return "Last Name can't be emptysdf.";
                          }}
                          hasError={!state.member.lastName.isValid}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <DropDownList
                          label={t("Clients.gender")}
                          data={GENDER_DDL}
                          value={state.member.gender.value}
                          onChange={(event) =>
                              update("member", "gender", event.target.value)
                          }
                          reference={setOpenedDropdownList}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <DropDownList
                          label={t("ClientDetails.Profile.Family.relationship")}
                          data={RELATIONSHIP_DDL}
                          textField={"name"}
                          dataItemKey={"id"}
                          value={state.member.relationShip}
                          onChange={(event) =>
                              updateValue("member", "relationShip", event.target.value)
                          }
                          reference={setOpenedDropdownList}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} className="date-of-birth-div">
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <NativeDatePicker
                          className={
                            state.member.dateOfBirth.value == null &&
                            !state.member.isNew
                                ? "MuiInput-root invalid MuiInput-underline Mui-error invalid"
                                : null
                          }
                          label={t("Clients.date_of_birth")}
                          selectedDate={
                            state.member.dateOfBirth.value
                                ? state.member.dateOfBirth.value
                                : null
                          }
                          maxDate={formatTimestampToDate(
                              new Date().getTime(),
                              "yyyy-MM-dd"
                          )}
                          onSelect={updateDateOfBirth("member", "dateOfBirth")}
                          hasError={
                            !(
                                state.member.dateOfBirth.isValid ||
                                !state.member.dateOfBirth.value
                            )
                          }
                          minDateMessage="Date of Birth cannot be before 1900"
                          maxDateMessage="Date of Birth cannot be after today"
                      />
                      {state.member.dateOfBirth.value == null &&
                          !state.member.isNew && (
                              <span className="invalid">
                      Date of Birth field can’t be empty
                    </span>
                          )}
                    </FormControl>
                  </Grid>

                  {state.member.gender.value === "Other" && (
                      <React.Fragment>
                        <Grid item xs={4}>
                          <FormControl
                              margin="normal"
                              required
                              className="percent-95-width"
                          >
                            <TextBox
                                label={"Other Gender"}
                                minLength={0}
                                maxLength={30}
                                value={state.member.genderOther.value}
                                onChange={(event) =>
                                    update("member", "genderOther", event.target.value)
                                }
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={8}></Grid>
                      </React.Fragment>
                  )}
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          label={t("ClientDetails.Profile.Family.company")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.company.name}
                          onChange={(event) =>
                              updateCompany("name", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          label={t("ClientDetails.Profile.Family.website")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.company.website}
                          onChange={(event) =>
                              updateCompany("website", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={8}></Grid>
                  <Grid item xs={4}>
                    {relationshipOtherComponent}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  {setupContactInfoComponent("member")}
                </Grid>
              </Paper>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );

  return (
    <div className="add-other-member-modal-container">
      <Typography component={"h1"} variant={"h1"}>
        {modalTitle}
      </Typography>
      {content}
    </div>
  );
};

export default AddOtherMemberForm;
